import React, { useEffect } from 'react';
import loadable from '@loadable/component';

import { bool, object } from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';

import { camelize } from '../../util/string';
import { propTypes } from '../../util/types';

import FallbackPage from './FallbackPage';
import { ASSET_NAME } from './LandingPage.duck';
import { fetchFeaturedListings } from '../../ducks/featuredListings.duck';
import { getListingsById } from '../../ducks/marketplaceData.duck';
import { getFeaturedListingsProps } from '../../util/data';

const PageBuilder = loadable(() =>
  import(/* webpackChunkName: "PageBuilder" */ '../PageBuilder/PageBuilder')
);

const ZAPIER_SCRIPT_SRC =
  'https://interfaces.zapier.com/assets/web-components/zapier-interfaces/zapier-interfaces.esm.js';
const ZAPIER_CHATBOT_TAG = 'zapier-interfaces-chatbot-embed';

// Mounts the Zapier chatbot popup only while the LandingPage is on
// screen. Other pages (notably ListingPage with its mobile sticky CTA)
// were getting the chatbot avatar overlapping their bottom-anchored
// actions, so we scope it to "/" only.
const useZapierChatbot = () => {
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    if (!document.querySelector(`script[data-zapier-chatbot]`)) {
      const script = document.createElement('script');
      script.async = true;
      script.type = 'module';
      script.src = ZAPIER_SCRIPT_SRC;
      script.dataset.zapierChatbot = '';
      document.head.appendChild(script);
    }

    let chatbot = document.querySelector(ZAPIER_CHATBOT_TAG);
    if (!chatbot) {
      chatbot = document.createElement(ZAPIER_CHATBOT_TAG);
      chatbot.setAttribute('is-popup', 'true');
      chatbot.setAttribute('chatbot-id', 'cm1c08ki1000asbh6bdvm0ess');
      chatbot.setAttribute('height', '600px');
      chatbot.setAttribute('width', '400px');
      document.body.appendChild(chatbot);
    }

    return () => {
      const el = document.querySelector(ZAPIER_CHATBOT_TAG);
      if (el) el.remove();
    };
  }, []);
};

export const LandingPageComponent = props => {
  const { pageAssetsData, inProgress, error } = props;

  useZapierChatbot();

  return (
    <PageBuilder
      pageAssetsData={pageAssetsData?.[camelize(ASSET_NAME)]?.data}
      inProgress={inProgress}
      error={error}
      fallbackPage={<FallbackPage error={error} />}
      featuredListings={getFeaturedListingsProps(camelize(ASSET_NAME), props)}
      isLandingPage={true}
    />
  );
};

LandingPageComponent.propTypes = {
  pageAssetsData: object,
  inProgress: bool,
  error: propTypes.error,
};

const mapStateToProps = state => {
  const { pageAssetsData, inProgress, error } = state.hostedAssets || {};
  const featuredListingData = state.featuredListings || {};

  const getListingEntitiesById = listingIds => getListingsById(state, listingIds);

  return { pageAssetsData, featuredListingData, getListingEntitiesById, inProgress, error };
};

const mapDispatchToProps = dispatch => ({
  onFetchFeaturedListings: (sectionId, parentPage, listingImageConfig, allSections) =>
    dispatch(fetchFeaturedListings({ sectionId, parentPage, listingImageConfig, allSections })),
});

// Note: it is important that the withRouter HOC is **outside** the
// connect HOC, otherwise React Router won't rerender any Route
// components since connect implements a shouldComponentUpdate
// lifecycle hook.
//
// See: https://github.com/ReactTraining/react-router/issues/4671
const LandingPage = compose(
  connect(
    mapStateToProps,
    mapDispatchToProps
  )
)(LandingPageComponent);

export default LandingPage;
